import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";
import Divider from "@material-ui/core/Divider";
import { makeStyles, withStyles } from "@material-ui/styles";
import Button from "components/button";
import Checkbox from "components/checkbox";
import DateRange from "components/date-range";
import { IconAlert } from "components/icons/icn-alert.icon";
import Input from "components/input";
import InputNumber from "components/input-number";
import { SelectBox } from "components/uikit-adapter";
import { useOrdersContext } from "features/orders/context";
import { useGlobalContext } from "hooks/useGlobalContext";
import { parseFloatByLocale } from "locales/utils";
import { GeneralSettingParameter } from "models/generalSetting/generalSetting";
import moment from "moment";
import PropTypes from "prop-types";
import { useEffect, useMemo, useState } from "react";
import intl from "react-intl-universal";
import { getGeneralSettingValue } from "services/general-settings/general-settings";
import { userAmbev, userFornecedor } from "services/permissions/getPermissions";
import { fetchSupplierByMaterials } from "services/supplier.service";
import { ISO_DATE_FORMAT } from "utils/format-date";
import {
  useAllowUserToSkipSupplierApproval,
  usesHourlyPlan,
} from "utils/validations-general-settings";
import { StatusPedido } from "../../../../models/pedidos/statusPedido";
import * as S from "./styled";
import { OrdersChangeDateTable } from "./table";
import {
  applyMatchedOrigin,
  findSupplierInItem,
  getValidationForInTransitStatus,
  resetItemOriginAndDates,
} from "./utils";

const StyledDialogContent = withStyles(() => ({
  root: {
    borderBottom: "none",
    height: "calc(100% - 105px)",
  },
}))(DialogContent);

const useStyles = makeStyles({
  sectionDivider: {
    margin: "20px 0 28px",
    height: 3,
    backgroundColor: "#E0E0E0",
    borderRadius: 2,
  },
});

export const OrdersChangeDateModal = ({ open, close, data }) => {
  const classes = useStyles();
  const { startOperation, showFeedback } = useGlobalContext();
  const allowUserToSkipSupplierApproval = useAllowUserToSkipSupplierApproval();
  const canSkipSupplierApproval = (item) =>
    item.NegociacaoPedido !== false && !item.IsBreweryTransfer &&
    item.SapCode && [1, 2].includes(item.OrderType);
  const { justifications } = useOrdersContext();
  const isUsePoAutomations = getGeneralSettingValue(GeneralSettingParameter.UsePoAutomations);

  const [alterableItems, setAlterableItems] = useState([]);
  const [dates, setDates] = useState({ collect: null, delivery: null });
  const [notes, setNotes] = useState("");
  const [updateQuantity, setUpdateQuantity] = useState(false);
  const [hasOrdersChangedInline, setHasOrdersChangedInline] = useState(false);
  const [showSkipApproval, setShowSkipApproval] = useState(false);
  const [skipSupplierApprovalByOrder, setSkipSupplierApprovalByOrder] = useState({});
  const [justificativaGeral, setJustificativaGeral] = useState("");
  const [generalNewOrigin, setGeneralNewOrigin] = useState(null);
  const [generalNewQuantity, setGeneralNewQuantity] = useState(null);
  const [generalDeliveryTime, setGeneralDeliveryTime] = useState(null);

  const handleJustificativaGeralChange = (value) => {
    setJustificativaGeral(value);
    setAlterableItems((prev) =>
      prev.map((item) => ({
        ...item,
        justificativa: value,
      }))
    );
  };

  const handleJustificativaChangeToARow = (row, value) => {
    setAlterableItems((prev) =>
      prev.map((item) => (item === row ? { ...item, justificativa: value } : item))
    );
  };

  const handleNewCollectDate = (collect) => {
    setAlterableItems(
      alterableItems.map((row) => {
        const isInTransitStatus = getValidationForInTransitStatus(row.OrderStatus);
        return {
          ...row,
          newCollectDate: isInTransitStatus ? null : collect,
          newDeliveryDate: isInTransitStatus
            ? null
            : moment(collect).add(row.TransitTime, "d").format(ISO_DATE_FORMAT),
        };
      })
    );
  };

  const handleNewCollectDateToARow = (row, collect) => {
    let _collectDate = collect;
    let _deliveryDate = null;

    if (collect) {
      const isInTransitStatus = getValidationForInTransitStatus(row.OrderStatus);
      _collectDate = isInTransitStatus ? null : collect;
      _deliveryDate = isInTransitStatus
        ? null
        : moment(_collectDate).add(row.TransitTime, "d").format(ISO_DATE_FORMAT);
    } else {
      _collectDate = null;
      _deliveryDate = null;
    }

    setAlterableItems(
      alterableItems.map((item) =>
        item === row
          ? {
              ...item,
              newCollectDate: _collectDate,
              newDeliveryDate: _deliveryDate,
            }
          : item
      )
    );
  };

  const handleNewDeliveryDate = (delivery) => {
    setAlterableItems(
      alterableItems.map((row) => {
        const isInTransitStatus = getValidationForInTransitStatus(row.OrderStatus);

        return {
          ...row,
          newCollectDate: isInTransitStatus
            ? null
            : moment(delivery).subtract(row.TransitTime, "d").format(ISO_DATE_FORMAT),
          newDeliveryDate: delivery,
        };
      })
    );
  };

  const handleNewDeliveryDateToARow = (row, delivery) => {
    let _deliveryDate = delivery;
    let _collectDate = null;

    if (delivery) {
      const isInTransitStatus = getValidationForInTransitStatus(row.OrderStatus);
      _collectDate = isInTransitStatus
        ? null
        : moment(delivery).subtract(row.TransitTime, "d").format(ISO_DATE_FORMAT);
      _deliveryDate = delivery;
    } else {
      _collectDate = null;
      _deliveryDate = null;
    }

    setAlterableItems(
      alterableItems.map((item) =>
        item === row
          ? {
              ...item,
              newCollectDate: _collectDate,
              newDeliveryDate: _deliveryDate,
            }
          : item
      )
    );
  };

 const handleNewDeliveryTimeToARow = (row, time) => {
  const normalizedTime = time || null;

  setAlterableItems((prev) =>
    prev.map((item) => {
      if (item.Id !== row.Id) return item;
      return {
        ...item,
        newDeliveryTime: normalizedTime,
      };
    })
  );
};
  
  const handleNewQuantity = (newOrderQuantity, index) => {
    setAlterableItems(
      alterableItems.map((row, _index) => ({
        ...row,
        newOrderQuantity:
          index === _index ? newOrderQuantity : alterableItems[_index].newOrderQuantity,
      }))
    );
  };

  const canReceiveGeneralOrigin = (item) =>
    item?.OrderStatus === StatusPedido.AguardandoAvisoRecebimento &&
    Array.isArray(item?.SupplierList) &&
    item.SupplierList.length > 0;

  const handleNewOrigin = (row, newOrigin) => {
    const id = newOrigin?.Id;
    if (!id) {
      setAlterableItems((prev) =>
        prev.map((item) => (item.Id === row.Id ? resetItemOriginAndDates(item) : item))
      );
      return;
    }

    const match = findSupplierInItem(row, id);

    if (!match) {
      setAlterableItems((prev) =>
        prev.map((item) => (item.Id === row.Id ? resetItemOriginAndDates(item) : item))
      );
      return;
    }

    setAlterableItems((prev) =>
      prev.map((item) => (item.Id === row.Id ? applyMatchedOrigin(item, match) : item))
    );
  };

  const supplierOptions = useMemo(() => {
    const list = alterableItems
      .filter((i) => Array.isArray(i.SupplierList))
      .flatMap((i) => i.SupplierList || []);
    const map = new Map();
    list.forEach((s) => {
      if (!map.has(s.Id)) map.set(s.Id, s);
    });
    return Array.from(map.values());
  }, [alterableItems]);

  const handleGeneralNewOriginChange = (selected) => {
    const id = selected?.Id;

    if (!id) {
      setGeneralNewOrigin(null);
      setAlterableItems((prev) =>
        prev.map((item) => (canReceiveGeneralOrigin(item) ? resetItemOriginAndDates(item) : item))
      );
      return;
    }

    setGeneralNewOrigin(id);
    setAlterableItems((prev) =>
      prev.map((item) => {
        if (!canReceiveGeneralOrigin(item)) return item;

        const match = findSupplierInItem(item, id);
        return match ? applyMatchedOrigin(item, match) : resetItemOriginAndDates(item);
      })
    );
  };

  const clearJustifications = () => {
    setJustificativaGeral("");
    setAlterableItems((prev) =>
      prev.map((item) => ({
        ...item,
        justificativa: "",
        newCollectDate: null,
        newDeliveryDate: null,
        newOrderQuantity: undefined,
        SupplierList: undefined,
        newOrigin: undefined,
        newDeliveryTime: undefined,
      }))
    );
    setGeneralNewOrigin(null);
  };

  const cleanDates = () => {
    setAlterableItems(
      alterableItems.map((row) => ({
        ...row,
        newCollectDate: null,
        newDeliveryDate: null,
      }))
    );
  };

  const clearInputs = () => {
    setDates({ collect: null, delivery: null });
    setNotes("");
    setUpdateQuantity(false);
    setSkipSupplierApprovalByOrder({});
    clearJustifications();
    setGeneralNewQuantity(null);
    setGeneralDeliveryTime(null);
  };

  const handleClose = () => {
    clearInputs();
    close();
  };

  const validateNewPickUpDate = () => {
    const isPickUpDateValid = alterableItems.every((item) => {
      if (getValidationForInTransitStatus(item.OrderStatus)) {
        return item.newCollectDate == null;
      } else {
        if (!item.newCollectDate) return true;
        return (
          moment(item.newCollectDate).format("YYYY-MM-DD") >=
          moment().format("YYYY-MM-DD")
        );
      }
    });

    let _errorMessage = "";
    if (!isPickUpDateValid)
      _errorMessage = intl.get("feedbacks.messageNewPickupDateValidation2");

    return {
      errorMessage: _errorMessage,
      result: isPickUpDateValid,
    };
  };

  const validateJustification = () => {
    const itemsAlterados = alterableItems.filter(
      (item) =>
        item.newCollectDate || item.newDeliveryDate || item.newOrderQuantity || item.newOrigin
    );

    const doesNotcontainJustification = itemsAlterados.every(
      (item) => !!item.justificativa
    );

    return {
      result: doesNotcontainJustification,
      errorMessage: intl.get("feedbacks.messageJustificationRequired"),
    };
  };


  const validateNewQuantity = () => {
    const isQuantityValid = !alterableItems.find(
      (x) => parseFloatByLocale(x.newOrderQuantity) === 0
    );

    let _errorMessage = "";
    if (!isQuantityValid)
      _errorMessage = intl.get("feedbacks.messageNewQuantityValidation");

    return {
      errorMessage: _errorMessage,
      result: isQuantityValid,
    };
  };

const buildBody = (item) => {
  const isInTransitStatus = getValidationForInTransitStatus(item.OrderStatus);

  const pickupDate = item.newCollectDate || item.CollectDate;
  const deliveryDate = item.newDeliveryDate || item.DeliveryDate;

  let _body = {
    OrderId: item.Id,
    Status: item.OrderStatus,
    PickupDate: pickupDate ? moment(pickupDate).format(ISO_DATE_FORMAT) : null,
    DeliveryDate: deliveryDate ? moment(deliveryDate).format(ISO_DATE_FORMAT) : null,
    ...(allowUserToSkipSupplierApproval && !userFornecedor() &&
      canSkipSupplierApproval(item)
      ? { SkipSupplierApproval: skipSupplierApprovalByOrder[item.Id] === true }
      : {}),
    JustificationId: item.justificativa ?? null,
    SupplierId: item.newOrigin ?? null,
  };

  if (notes) _body = { ..._body, Notes: notes };

  if (item.newOrderQuantity) {
    _body = { ..._body, Quantity: parseFloatByLocale(item.newOrderQuantity) };
  }

  if (item.newDeliveryTime) {
    _body = { ..._body, DeliveryTime: item.newDeliveryTime };
  }

  if (isInTransitStatus) {
    delete _body.PickupDate;
  }

  return _body;
};

 const handleSave = () => {
    const isPickUpDateValid = validateNewPickUpDate();
    const isQuantityValid = validateNewQuantity();
    const isJustificationValid = validateJustification();

    if (!isPickUpDateValid.result || !isQuantityValid.result || !isJustificationValid.result) {
      showFeedback(
        isPickUpDateValid.errorMessage ||
          isQuantityValid.errorMessage ||
          isJustificationValid.errorMessage
      );
      return;
    }

    let requests = alterableItems
      .filter(
        (item) =>
          item.newCollectDate ||
          item.newDeliveryDate ||
          item.newOrderQuantity ||
          item.newOrigin ||
          item.newDeliveryTime
      )
      .map((item) => {
        const _body = buildBody(item);

        if (Object.keys(_body).length === 1) return null;

        return {
          API: `orders/${item.Id}:update-dates`,
          method: "patch",
          body: _body,
          errorId: item.Id,
        };
      });

    try {
      requests = requests.filter(Boolean);
    } catch (error) {
      console.error(error);
    }

    if (requests.length === 0) {
      return;
    }

    const text = intl.get("orders.operationText01");
    startOperation(requests, text);

    clearInputs();
    close();
  };
  
  useEffect(() => {
    if (dates.collect) {
      handleNewCollectDate(dates.collect);
    } else if (dates.delivery) {
      handleNewDeliveryDate(dates.delivery);
    } else {
      cleanDates();
    }
  }, [dates]);

  useEffect(() => {
    setAlterableItems(data);
    if (open) {
      setSkipSupplierApprovalByOrder(
        Object.fromEntries(data.map((item) => [item.Id, false]))
      );
    }
  }, [data, open]);

  const shouldLoadSuppliers = (isOpen, items) => {
    if (!isOpen) return false;
    return Array.isArray(items) && items.length > 0;
  };

  const getFilteredItemsForSupplier = (items) => {
    return items;
  };

  const findSupplierByMaterialId = (suppliers, materialId) => {
    return suppliers.find((supplier) => supplier.MaterialId === materialId);
  };

  const mapItemWithSupplier = (item, suppliers) => {
    const supplierData = findSupplierByMaterialId(suppliers, item.MaterialId);
    if (supplierData) {
      return { ...item, SupplierList: supplierData.suppliers };
    }
    return item;
  };

  const updateItemsWithSuppliers = (suppliers) => {
    setAlterableItems((prev) => prev.map((item) => mapItemWithSupplier(item, suppliers)));
  };

  const loadSuppliers = async (items) => {
    const filteredItems = getFilteredItemsForSupplier(items);
    if (filteredItems.length === 0) return;

    const params = { MaterialIds: filteredItems.map((x) => x.MaterialId) };
    const suppliers = await fetchSupplierByMaterials(params);
    if (!suppliers || suppliers.length === 0) return;

    updateItemsWithSuppliers(suppliers);
  };

  const handleChangeGeneralNewQuantity = (value) => {
    setGeneralNewQuantity(value);
    setAlterableItems((prev) =>
      prev.map((item) => ({
        ...item,
        newOrderQuantity: !getValidationForInTransitStatus(item.OrderStatus) ? value : item?.newOrderQuantity,
      }))
    );
  };

const handleChangeGeneralDeliveryTime = (value) => {
  const normalizedValue = value || null;
  setGeneralDeliveryTime(normalizedValue);

  setAlterableItems((prev) =>
    prev.map((item) => ({
      ...item,
      newDeliveryTime: item.OrderDeliveryTime ? normalizedValue : undefined,
    }))
  );
};

  useEffect(() => {
    if (!isUsePoAutomations) return;
    if (!shouldLoadSuppliers(open, alterableItems)) return;
    loadSuppliers(alterableItems);
  }, [open]);

  useEffect(() => {
    const hasQuantityChanged = alterableItems.find(
      (x) => x.newOrderQuantity && parseFloatByLocale(x.newOrderQuantity) !== 0
    );

    const hasChangedInline = alterableItems.filter(
      (alterableItem) =>
        alterableItem.newCollectDate ||
        alterableItem.newDeliveryDate ||
        alterableItem.newOrderQuantity ||
        alterableItem.newOrigin ||
        alterableItem.newDeliveryTime
    );

    const hasNegotiableSapOrder = alterableItems.some(canSkipSupplierApproval);

    const isNotSupplier = !userFornecedor();

    setUpdateQuantity(!!hasQuantityChanged);
    setHasOrdersChangedInline(hasChangedInline.length > 0);
    setShowSkipApproval(hasNegotiableSapOrder && isNotSupplier);
  }, [alterableItems]);

  const renderJustificationAlone = ((!isUsePoAutomations || !userFornecedor()) && !userAmbev());

  return (
    <Dialog fullScreen open={open}>
      <S.DialogTitle>
        <S.DialogTitleText>{intl.get("orders.changeOrder")}</S.DialogTitleText>
        <S.DialogTitleIcon onClick={handleClose} />
      </S.DialogTitle>
      <StyledDialogContent dividers>
        <OrdersChangeDateTable
          data={alterableItems}
          justificationOptions={justifications}
          handleNewQuantity={handleNewQuantity}
          handleNewCollectDateToARow={handleNewCollectDateToARow}
          handleNewDeliveryDateToARow={handleNewDeliveryDateToARow}
          handleJustificativaChangeToARow={handleJustificativaChangeToARow}
          handleNewOrigin={handleNewOrigin}
          handleNewDeliveryTimeToARow={handleNewDeliveryTimeToARow}
        />

        {showSkipApproval && allowUserToSkipSupplierApproval && alterableItems
          .filter(canSkipSupplierApproval)
          .map((item) => (
            <div className="dialog__content--checkbox" key={item.Id}>
              <Checkbox
                text={intl.get("orders.skipSupplierApproval") + (alterableItems.length > 1 ? ` - ${item.SapCode || item.Id}` : "")}
                name={`skip-supplier-approval-${item.Id}`}
                checked={skipSupplierApprovalByOrder[item.Id] === true}
                onChange={(event) => setSkipSupplierApprovalByOrder((prev) => ({ ...prev, [item.Id]: event.target.checked }))}
              />
              {skipSupplierApprovalByOrder[item.Id] === true && (
                <span>{intl.get("orders.skipSupplierApprovalWarning")}</span>
              )}
            </div>
          ))}

        <Divider className={classes.sectionDivider} variant="fullWidth" />

        <S.DialogContentTile>{intl.get("orders.changeDateOfMultipleOrders")}</S.DialogContentTile>

        <S.DialogContentSection>
          <div className="dialog__content--main">
            <div className="dialog__content--text">
              <div className="dialog__content--text--title">
                <IconAlert color={"var(--color-feedback-danger-300)"} />
                <h4>{intl.get("orders.operationHeader")}</h4>
              </div>
              <p>{intl.get("orders.operationContent")}</p>
            </div>

            <div className="dialog__content--action">
              <div
                className={`dialog__content--action-fisrt-line ${renderJustificationAlone ? "one-column-full-width" : ""}`}
              >
                {isUsePoAutomations && userFornecedor() && (
                  <SelectBox
                    name="nova-origem-geral"
                    label={intl.get("orders.newOrigin")}
                    placeholder={intl.get("commons.selectJustification")}
                    valueKey="Id"
                    labelKey="ShortDescription"
                    value={
                      generalNewOrigin
                        ? supplierOptions.find((o) => o.Id === generalNewOrigin) || null
                        : null
                    }
                    options={supplierOptions}
                    onChange={(selected) => handleGeneralNewOriginChange(selected || null)}
                    searchable
                    clearable
                  />
                )}

                <SelectBox
                  name="justificativa-geral"
                  label={intl.get("commons.justification")}
                  placeholder={intl.get("commons.selectJustification")}
                  valueKey="Id"
                  labelKey="Description"
                  value={
                    justificativaGeral
                      ? {
                          Id: justificativaGeral,
                          Description: justifications.find((o) => o.Id === justificativaGeral)
                            ?.Description,
                        }
                      : null
                  }
                  options={justifications}
                  onChange={(selected) =>
                    handleJustificativaGeralChange(selected ? selected.Id : "")
                  }
                  searchable
                  clearable
                />

                {userAmbev() && (
                  <InputNumber
                    label={intl.get("orders.newQuantity")}
                    value={generalNewQuantity}
                    onChange={(value) => handleChangeGeneralNewQuantity(value)}
                    type="default"
                    allowDecimals
                    decimalsLimit={3}
                    data-testid="input-general-new-quantity"
                  />
                )}
              </div>
              <div className="date-time-row">
                <div className="date-range">
                  <DateRange
                    labelDe={intl.get("stocks.informAdvanceOrder.newCollectionDate")}
                    labelAte={intl.get("stocks.informAdvanceOrder.newDeliveryDate")}
                    dataDe={dates.collect}
                    dataAte={dates.delivery}
                    onChangeDataDe={(date) => setDates({ collect: date, delivery: null })}
                    onChangeDataAte={(date) => setDates({ collect: null, delivery: date })}
                    minDate={moment().format(ISO_DATE_FORMAT)}
                  />
                </div>

                {usesHourlyPlan() && (
                  <div className="delivery-time">
                    <Input
                      label={intl.get("stocks.informAdvanceOrder.newDeliveryTime")}
                      name="generalDeliveryTime"
                      htmlType="time"
                      value={generalDeliveryTime || ""}
                      onChange={(event) => handleChangeGeneralDeliveryTime(event.target.value)}
                    />
                  </div>
                )}
              </div>
              <Input
                label={intl.get("commons.observation")}
                value={notes}
                onChange={(event) => setNotes(event.target.value)}
                data-testid="input-note"
              />
            </div>
          </div>
        </S.DialogContentSection>
      </StyledDialogContent>

      <S.DialogActions>
        <Button
          buttonType="default"
          value={intl.get("geral.buttonsDefault.cancel")}
          onClick={handleClose}
        />
        <Button
          buttonType="primary"
          value={intl.get("commons.confirmUpdate")}
          onClick={handleSave}
          disabled={!updateQuantity && !hasOrdersChangedInline}
        />
      </S.DialogActions>
    </Dialog>
  );
};

OrdersChangeDateModal.propTypes = {
  close: PropTypes.func.isRequired,
  data: PropTypes.any.isRequired,
  open: PropTypes.any.isRequired,
};
